import React, { memo, useState } from "react";
import {
    Typography,
    Box,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Theme,
} from "@mui/material";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { InfoOutlined } from "@mui/icons-material";

const FOOTER_HEIGHT = '50px';
const CSL_DISCLAIMER_TEXT = "CSL is not intended to be used for treatment, diagnosis, prevention, or monitoring of any disease. The data from CSL should not be relied on to make medical decisions. This data is only intended to be used for troubleshooting and support purposes. Any action taken on the metra device should be based on the clinician’s judgement considering their training, expertise, and guidance on how to use the device from OrganOx.  The CSL app is not connected to the metra device and is not intended by OrganOx to be used as an accessory of the metra device.";

const SIDEBAR_OFFSET_MD = '70px';
const SIDEBAR_OFFSET_LG = '80px';

const getFooterRootStyles = (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    backgroundColor: "#5A5A5A",
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    marginBottom:{xs:"0.2rem", md:0},
    padding: theme.spacing(0, 0.5),
    zIndex: theme.zIndex.appBar,
    height: FOOTER_HEIGHT,
    overflowX: 'auto',
    overflowY: 'hidden',
});

const scrollableContentStyles = (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: {
        xs: theme.spacing(1.5),
        sm: SIDEBAR_OFFSET_MD,
        md: SIDEBAR_OFFSET_MD,
        lg: SIDEBAR_OFFSET_LG,
        xl: SIDEBAR_OFFSET_LG,
    },
    paddingRight: theme.spacing(1.5),
    gap: theme.spacing(2),
});

const itemStyles = {
    flexShrink: 0,
    whiteSpace: 'nowrap',
    color: "#FFFFFFB2",
    display: 'flex',
    alignItems: 'center',
};

interface DefaultProps {
    containerStyle?: React.CSSProperties | object;
}

interface BuildInfo {
    buildId?: string;
    buildNumber?: string;
    commitHash?: string;
    branchName?: string;
}

const Footer: React.FC<DefaultProps> = ({ containerStyle }): JSX.Element => {
    const [openInfoDialog, setOpenInfoDialog] = useState(false);
    const [openDisclaimerDialog, setOpenDisclaimerDialog] = useState(false);

    const [buildInfo] = useState<BuildInfo>({
        buildId: process.env.REACT_APP_BUILD_ID ?? "N/A",
        buildNumber: process.env.REACT_APP_BUILD_NUMBER ?? "N/A",
        commitHash: process.env.REACT_APP_COMMIT_HASH ?? "N/A",
        branchName: process.env.REACT_APP_BRANCH_NAME ?? "N/A",
    });

    const handleOpenInfoDialog = () => setOpenInfoDialog(true);
    const handleCloseInfoDialog = () => setOpenInfoDialog(false);
    const handleOpenDisclaimerDialog = () => setOpenDisclaimerDialog(true);
    const handleCloseDisclaimerDialog = () => setOpenDisclaimerDialog(false);

    if (typeof global !== "undefined" && (global as any).__TEST_HOOKS__) {
        (global as any).__TEST_HOOKS__.footerHandleOpenInfoDialog = handleOpenInfoDialog;
        (global as any).__TEST_HOOKS__.footerHandleCloseInfoDialog = handleCloseInfoDialog;
        (global as any).__TEST_HOOKS__.footerHandleOpenDisclaimerDialog = handleOpenDisclaimerDialog;
        (global as any).__TEST_HOOKS__.footerHandleCloseDisclaimerDialog = handleCloseDisclaimerDialog;
    }

    return (
        <>
            <Box
                component="footer"
                sx={(theme) => ({
                    ...getFooterRootStyles(theme),
                    ...(typeof containerStyle === 'function' ? containerStyle(theme) : containerStyle),
                })}
            >
                <Box
                    sx={(theme) => scrollableContentStyles(theme)}
                >
                    <Typography
                        variant="caption"
                        fontSize={{ xs: "12px", sm: "13px" }}
                        fontWeight={400}
                        lineHeight={"18px"}
                        sx={{
                            ...itemStyles,
                        }}
                    >
                        OrganOx Limited is registered in England and Wales (Reg No: 06557113) | Reg Office: 125 Wood Street, London, EC2V 7AW, UK | VAT No: 943672795.
                    </Typography>

                    <Box sx={itemStyles}>
                        <Typography
                            variant="caption"
                            fontSize={{ xs: "12px", sm: "13px" }}
                            fontWeight={400}
                            lineHeight={"18px"}
                            sx={{ mr: 0.25 }}
                        >
                            Build ID: {buildInfo.buildId}
                        </Typography>
                        <IconButton
                            data-testid="build-info-button"
                            aria-label="Show build information"
                            onClick={handleOpenInfoDialog}
                            sx={{ color: "white", p: 0.25 }}
                            size="small"
                        >
                            <InfoOutlined sx={{ fontSize: '1.1rem' }} />
                        </IconButton>
                    </Box>

                    <Box sx={itemStyles}>
                        <Typography
                            variant="caption"
                            fontSize={{ xs: "12px", sm: "13px" }}
                            fontWeight={800}
                            lineHeight={"18px"}
                            sx={{ mr: 0.25 }}
                        >
                            CSL Disclaimer
                        </Typography>
                        <IconButton
                            data-testid="csl-disclaimer-button"
                            aria-label="Show CSL disclaimer"
                            onClick={handleOpenDisclaimerDialog}
                            sx={{ color: "white", p: 0.25 }}
                            size="small"
                        >
                            <WarningAmberIcon sx={{ fontSize: '1.1rem' }} />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            <Dialog
                open={openInfoDialog}
                onClose={handleCloseInfoDialog}
                aria-labelledby="build-info-dialog-title"
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{
                    textAlign: 'center',
                    fontWeight: 'bold',
                }} id="build-info-dialog-title">Build Info</DialogTitle>
                <DialogContent >
                    <Box sx={{ minWidth: { xs: 260, sm: 300 } }}>
                        <Typography gutterBottom sx={{
                            fontSize: 'small',
                        }}>
                            Build Number: {buildInfo.buildNumber}
                        </Typography>
                        <Typography gutterBottom sx={{
                            fontSize: 'small',
                        }}>
                            Build ID: {buildInfo.buildId}
                        </Typography>
                        <Typography gutterBottom sx={{
                            fontSize: 'small',
                        }}>
                            Commit: {buildInfo.commitHash}
                        </Typography>
                        <Typography sx={{
                            fontSize: 'small',
                        }}>
                            Branch Name: {buildInfo.branchName}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center' }}>
                    <Button onClick={handleCloseInfoDialog} variant="contained" color="primary" autoFocus>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openDisclaimerDialog}
                onClose={handleCloseDisclaimerDialog}
                aria-labelledby="csl-disclaimer-dialog-title"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{
                    textAlign: 'center',
                    fontWeight: 'bold',
                }} id="csl-disclaimer-dialog-title">CSL Disclaimer</DialogTitle>
                <DialogContent >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', textAlign: 'justify' }}>
                        {CSL_DISCLAIMER_TEXT}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center' }}>
                    <Button onClick={handleCloseDisclaimerDialog} variant="contained" color="primary" autoFocus>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default memo(Footer);